/**
 * AI Generation Monitor Service
 *
 * Ensures AI text generation completes successfully and displays in the UI
 * without requiring manual debugging.
 *
 * Key responsibilities:
 * - Detect when AI generation has not completed
 * - Automatically retry generation with exponential backoff
 * - Provide clear error messages to users
 * - Log issues for debugging
 */

import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/utils/logger";

export interface AIGenerationStatus {
  hasSummary: boolean;
  hasActionItems: boolean;
  hasNotes: boolean;
  hasAnalytics: boolean;
  isComplete: boolean;
  missingFields: string[];
}

/**
 * Check if a meeting has complete AI-generated content
 */
export function checkAIGenerationStatus(meeting: any): AIGenerationStatus {
  const missingFields: string[] = [];

  const hasSummary = meeting?.summary && meeting.summary.trim().length > 0;
  const hasActionItems = meeting?.action_items && meeting.action_items.trim().length > 0;
  const hasNotes = meeting?.notes && meeting.notes.trim().length > 0;

  // Check for at least some analytics
  const hasAnalytics =
    meeting?.sentiment_analysis ||
    meeting?.speaker_emotions ||
    meeting?.effectiveness_score;

  if (!hasSummary) missingFields.push("summary");
  if (!hasActionItems) missingFields.push("action_items");
  if (!hasNotes) missingFields.push("notes");

  return {
    hasSummary,
    hasActionItems,
    hasNotes,
    hasAnalytics,
    isComplete: hasSummary && hasActionItems && hasNotes,
    missingFields,
  };
}

// Must match the minimum enforced by the generate-all-insights edge function.
// Anything below it gets a 422 there, so retrying it can never succeed.
export const MIN_TRANSCRIPT_CHARS_FOR_AI = 100;
export const MIN_TRANSCRIPT_WORDS_FOR_AI = 20;

export function isTranscriptLongEnoughForAI(transcription: string | null | undefined): boolean {
  const trimmed = (transcription || "").trim();
  const wordCount = trimmed.split(/\s+/).filter(w => w.length > 0).length;
  return trimmed.length >= MIN_TRANSCRIPT_CHARS_FOR_AI && wordCount >= MIN_TRANSCRIPT_WORDS_FOR_AI;
}

/**
 * Validate that a meeting has the transcription needed for AI generation
 */
export function canGenerateAI(meeting: any): { can: boolean; reason?: string } {
  if (!meeting?.transcription) {
    return { can: false, reason: "No transcription available. Please ensure the recording was transcribed successfully." };
  }

  if (!isTranscriptLongEnoughForAI(meeting.transcription)) {
    return { can: false, reason: "There isn't enough conversation in this recording to summarize." };
  }

  return { can: true };
}

/**
 * Retry AI generation with exponential backoff
 * NOTE: This now delegates to the resilience service which provides:
 * - Persistent tracking of attempts in IndexedDB
 * - Exponential backoff with jitter
 * - Response validation
 * - Comprehensive error logging
 */
export async function retryAIGeneration(
  meetingId: string,
  transcription: string,
  template?: string,
  maxRetries: number = 3
): Promise<{ success: boolean; error?: string; isAuthError?: boolean }> {
  try {
    // Lazy load resilience service to avoid circular imports
    const { retryWithResilience } = await import('./ai-generation-resilience');
    return await retryWithResilience(meetingId, transcription, template, maxRetries);
  } catch (err) {
    logger.error('[AI-Monitor] Failed to use resilience service:', err);
    // Fallback to basic retry if resilience service fails
    return { success: false, error: String(err) };
  }
}

/**
 * Monitor a meeting for incomplete AI generation and retry if needed
 */
export async function monitorAIGeneration(
  meetingId: string,
  meetingData: any,
  onStatusChange?: (status: AIGenerationStatus) => void
): Promise<AIGenerationStatus> {
  const status = checkAIGenerationStatus(meetingData);
  onStatusChange?.(status);

  if (status.isComplete) {
    logger.log(`[AI-Monitor] Meeting ${meetingId} has complete AI generation`);
    return status;
  }

  logger.warn(`[AI-Monitor] Meeting ${meetingId} missing AI fields:`, status.missingFields);

  // Check if we can retry
  const canGenerate = canGenerateAI(meetingData);
  if (!canGenerate.can) {
    logger.error(`[AI-Monitor] Cannot retry generation: ${canGenerate.reason}`);
    return status;
  }

  // Automatically retry
  logger.log(`[AI-Monitor] Initiating automatic retry for meeting ${meetingId}`);
  const result = await retryAIGeneration(
    meetingId,
    meetingData.transcription,
    meetingData.template
  );

  if (!result.success) {
    logger.error(`[AI-Monitor] Automatic retry failed: ${result.error}`);
  }

  return status;
}
